import { isExpired } from "react-jwt";
import apiClient, {
  API_ENDPOINTS,
  appendPath,
  dispatchAndResolve,
  executeApiRequest,
  rejectWithError
} from "api";
import keys from "config/keys.jsx";
import { buildUserCartItemPayload } from "domains/commerce/services/cartRequestBuilders";
import {
  buildCompanyUserCriteria,
  buildShoppingCartLineItemPayload,
  buildStudentAttendanceCriteria,
  buildUploadContentFormData,
  buildVideoUploadFormData,
  buildUserLookupCriteria
} from "domains/user/services/userRequestBuilders";
import { setLoginTransition } from "../../utils/loginTransition.jsx";
import { getCurrentRelativeUrl, storePendingNavigation } from "../../utils/pendingNavigation.js";
import { markAccessTokenIssued, refreshAuthSession } from "../../auth/sessionCoordinator";
import * as types from "./types.jsx";

const { auth, attendance, curriculum, notification, payment, upload, users } = API_ENDPOINTS;

const getStorage = () => (typeof window !== "undefined" ? window.localStorage : null);
const getSessionStorage = () => (typeof window !== "undefined" ? window.sessionStorage : null);
const PUBLIC_TENANT_STORAGE_KEYS = [
  "subdomain",
  "domainType",
  "CompPrimaryId",
  "companyLogo",
  "dynamicAttributes",
  "flexOfficeThemeMode"
];
const AUTH_LOGOUT_STORAGE_KEY = "avpsLoggedOut";

export const PAYMENT_ERROR_MESSAGE = "We couldn’t process your payment at this time. Please try again in a few moments.";

const isTransientPaymentError = (error) => {
  const status = Number(error?.status);
  return !Number.isFinite(status) || status >= 500;
};

const toUserFacingPaymentError = (cause) => {
  const error = new Error(PAYMENT_ERROR_MESSAGE);
  error.name = "PaymentError";
  error.cause = cause;
  error.status = cause?.status;
  return error;
};

const getToken = () => {
  try {
    return getStorage()?.getItem("token") || "";
  } catch {
    return "";
  }
};

const unwrapApiResponse = (payload) => {
  if (payload && Object.prototype.hasOwnProperty.call(payload, "data")) {
    return payload.data;
  }
  return payload;
};

const getErrorPayload = (error) => error?.payload || error?.message || error;

const rejectAction = (dispatch, failType, error) => {
  if (failType) {
    dispatch({ type: failType, payload: getErrorPayload(error) });
  }
  return rejectWithError(error);
};

const resolveAction = (dispatch, type, payload) => dispatchAndResolve(dispatch, type, payload);

const getAuthTokenFromPayload = (payload) => (
  payload?.token
  || payload?.accessToken
  || payload?.accessConfirmationToken
  || payload?.data?.accessConfirmationToken
  || payload?.data?.accessToken
  || payload?.data?.token
  || payload?.jwtResponse?.jwtToken
  || payload?.data?.jwtResponse?.jwtToken
  || payload?.data?.userDetails?.accessConfirmationToken
  || payload?.data?.userDetails?.accessToken
  || payload?.data?.userDetails?.token
  || payload?.data?.userDetails?.jwtResponse?.jwtToken
  || payload?.payload?.accessConfirmationToken
  || payload?.payload?.accessToken
  || payload?.payload?.token
  || payload?.payload?.jwtResponse?.jwtToken
  || payload?.payload?.data?.accessConfirmationToken
  || payload?.payload?.data?.accessToken
  || payload?.payload?.data?.token
  || payload?.payload?.data?.jwtResponse?.jwtToken
  || ""
);

const getEmailFromPayload = (payload, fallbackEmail) => (
  payload?.email
  || payload?.data?.email
  || payload?.data?.userDetails?.email
  || payload?.payload?.data?.userDetails?.email
  || payload?.userDetails?.email
  || fallbackEmail
  || ""
);

const normalizeRoles = (...roleSources) => {
  const roles = roleSources
    .flatMap((source) => {
      if (Array.isArray(source)) return source;
      if (source === undefined || source === null || source === "") return [];
      return [source];
    })
    .map((role) => {
      if (typeof role === "string") return role;
      if (typeof role === "object") return role?.name || role?.value || role?.role || role?.userRole || "";
      return String(role || "");
    })
    .map((role) => String(role || "").trim().toUpperCase().replace(/^ROLE_/, ""))
    .filter(Boolean);

  return [...new Set(roles)];
};

const getRolesFromPayload = (payload = {}) => normalizeRoles(
  payload?.userRoles,
  payload?.userRole,
  payload?.roles,
  payload?.authorities,
  payload?.data?.userRoles,
  payload?.data?.userRole,
  payload?.data?.roles,
  payload?.data?.authorities,
  payload?.data?.userDetails?.userRoles,
  payload?.data?.userDetails?.userRole,
  payload?.data?.userDetails?.roles,
  payload?.data?.userDetails?.authorities,
  payload?.userDetails?.userRoles,
  payload?.userDetails?.userRole,
  payload?.userDetails?.roles,
  payload?.userDetails?.authorities
);

const storeSignInSession = (payload, fallbackEmail) => {
  const storage = getStorage();
  if (!storage) return;

  storage.removeItem(AUTH_LOGOUT_STORAGE_KEY);
  storage.removeItem("isTokenExpired");
  storage.removeItem("tokenExpired");
  const authToken = getAuthTokenFromPayload(payload);
  const email = getEmailFromPayload(payload, fallbackEmail);
  const roles = getRolesFromPayload(payload);

  // The backend stores the access token in an HttpOnly cookie and returns the
  // user payload separately. Track only the issuance time in the browser so
  // the session coordinator can schedule the next refresh without persisting
  // another credential.
  markAccessTokenIssued();

  if (roles.length > 1) {
    setLoginTransition("role-selection");
    if (authToken) storage.setItem("token", authToken);
    if (email) storage.setItem("email", email);
    storage.setItem("userRoles", JSON.stringify(roles));
    storage.removeItem("userData");
    storage.removeItem("isLoggedIn");
    storage.removeItem("isAuthenticated");
    return;
  }

  if (authToken) storage.setItem("token", authToken);
  if (email) storage.setItem("email", email);
  if (roles.length) storage.setItem("userRoles", JSON.stringify(roles));
  // Keep the browser session self-contained for the template chrome. The
  // backend normally authenticates with an HttpOnly cookie, but the reader
  // header must also be able to restore the signed-in state after a reload.
  storage.setItem("isLoggedIn", "true");
  storage.setItem("isAuthenticated", "true");
  if (payload && typeof payload === "object") {
    storage.setItem("userData", JSON.stringify(payload));
  }

  // Storage events do not fire in the tab that performed the login. Notify
  // mounted template chrome immediately after password, OTP, Google, or role
  // finalization reaches this common successful-session path.
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("avps-auth-updated"));
  }
};

const clearAuthStorage = ({ preserveCurrentPath = false } = {}) => {
  const storage = getStorage();
  const sessionStorage = getSessionStorage();

  if (storage) {
    const path = preserveCurrentPath && typeof window !== "undefined"
      ? getCurrentRelativeUrl(window.location)
      : storage.getItem("previous-path");
    const publicTenantState = PUBLIC_TENANT_STORAGE_KEYS.reduce((state, key) => {
      const value = storage.getItem(key);
      if (value !== null) state[key] = value;
      return state;
    }, {});

    storage.clear();
    Object.entries(publicTenantState).forEach(([key, value]) => storage.setItem(key, value));
    storage.setItem(AUTH_LOGOUT_STORAGE_KEY, "true");

    if (path) {
      storePendingNavigation(path);
    }
  }

  sessionStorage?.removeItem("recurrenceData");
  sessionStorage?.removeItem("pendingBooking");
  sessionStorage?.removeItem("googleAuthenticated");

  // `storage` events are not dispatched to the tab that made the change.
  // Notify mounted headers/components in this tab so they can immediately
  // recompute their authentication state after logout.
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("avps-auth-updated"));
  }
};

const hasValidToken = () => {
  const token = getToken();
  return token && !isExpired(token);
};

const createResponseLike = ({ response, payload }) => ({
  ok: response.ok,
  status: response.status,
  statusText: response.statusText,
  headers: response.headers,
  payload,
  json: () => Promise.resolve(payload),
  text: () => Promise.resolve(
    typeof payload === "string" ? payload : JSON.stringify(payload ?? "")
  )
});

const parseTextPayload = (payload) => {
  if (typeof payload !== "string") return payload;
  try {
    return JSON.parse(payload);
  } catch {
    return payload;
  }
};

const postAuthAction = ({ endpoint, payload, successType, failType, options, onSuccess }) => async (dispatch) => {
  try {
    const data = await apiClient.post(endpoint, payload, {
      credentials: "include",
      skipAuth: true,
      ...(options || {})
    });

    onSuccess?.(data);
    return resolveAction(dispatch, successType, data);
  } catch (error) {
    return rejectAction(dispatch, failType, error);
  }
};

export const signin = (user) => postAuthAction({
  endpoint: auth.login,
  payload: user,
  successType: types.SIGNIN_SUCCESS,
  failType: types.SIGNIN_FAIL,
  onSuccess: (data) => storeSignInSession(data, user?.email)
});

export const googleSignin = (userData) => postAuthAction({
  endpoint: auth.googleLogin,
  payload: userData,
  successType: types.SIGNIN_SUCCESS,
  failType: types.SIGNIN_FAIL,
  options: { query: { signInOption: "Google_login" } },
  onSuccess: (data) => {
    storeSignInSession(data, userData?.email);
    getSessionStorage()?.setItem("googleAuthenticated", "true");
  }
});

export const OtpLogin = (user) => postAuthAction({
  endpoint: auth.OtpLogin,
  payload: user,
  successType: types.SIGNIN_SUCCESS,
  failType: types.SIGNIN_FAIL,
  onSuccess: (data) => storeSignInSession(data, user?.email)
});

export const sendOtp = (user, signInOption = "Phone_otp") => async (dispatch) => {
  try {
    const data = await apiClient.post(auth.sendOtp, user, {
      credentials: "include",
      query: { signInOption },
      responseType: "text"
    });
    const payload = parseTextPayload(data);
    return resolveAction(dispatch, types.SEND_OTP_SUCCESS, payload);
  } catch (error) {
    return rejectAction(dispatch, types.SEND_OTP_FAIL, error);
  }
};

export const performLogout = async () => {
  try {
    await apiClient.post(auth.logout, undefined, { credentials: "include" });
    return "Logged out successfully";
  } catch (error) {
    return rejectWithError(error);
  }
};

export const AuthLogout = ({ reason = "session-expired" } = {}) => async (dispatch) => {
  dispatch({ type: types.LOGOUT_START, payload: { reason } });
  clearAuthStorage({ preserveCurrentPath: true });
  // Session expiry uses the same optimistic transition as an explicit logout:
  // the mounted UI must become anonymous before the best-effort API call.
  dispatch({ type: types.LOGOUT, payload: { reason } });
  try {
    return await performLogout();
  } finally {
    // The state is already cleared. Keep this idempotent dispatch so any
    // middleware/listeners that observe completion receive the same signal.
    dispatch({ type: types.LOGOUT, payload: { reason } });
  }
};

export const logout = ({ reason = "user-logout" } = {}) => async (dispatch) => {
  dispatch({ type: types.LOGOUT_START, payload: { reason } });
  clearAuthStorage();
  // Clear the mounted application state before waiting on the server. This is
  // intentionally optimistic so logout never requires a document reload or a
  // successful network response to update the visible UI.
  dispatch({ type: types.LOGOUT, payload: { reason } });

  try {
    await apiClient.post(auth.logout, undefined, { credentials: "include" });
    return "Logged out successfully";
  } catch (error) {
    return rejectWithError(error);
  }
};

export const refreshToken = () => async () => {
  try {
    const refreshed = await refreshAuthSession(keys.URL, { force: true });
    if (!refreshed) {
      throw new Error("Unable to refresh the authenticated session");
    }
    return { refreshed: true };
  } catch (error) {
    return rejectWithError(error);
  }
};

export const register = (user) => postAuthAction({
  endpoint: auth.register,
  payload: user,
  successType: types.REGISTER_SUCCESS,
  failType: types.REGISTER_FAIL
});

export const fetchStudent = (user) => async (dispatch) => {
  try {
    const data = await apiClient.post(auth.getUserByEmail, buildUserLookupCriteria(user));
    return resolveAction(dispatch, types.GET_USER_EMAIL, data);
  } catch (error) {
    return rejectAction(dispatch, types.REGISTER_FAIL, error);
  }
};

export const fetchStudentById = fetchStudent;

export const fetchUser = (user) => async () => {
  try {
    return await apiClient.post(auth.getUserByEmail, buildUserLookupCriteria(user));
  } catch (error) {
    return rejectWithError(error);
  }
};

export const subscribeUser = (user) => async (dispatch) => {
  try {
    const data = await apiClient.post(notification.queryEmail, user);
    return resolveAction(dispatch, types.SUBSCRIBED_SUCCESS, data);
  } catch (error) {
    return rejectAction(dispatch, types.SUBSCRIBED_FAIL, error);
  }
};

export const loadUserById = (_id) => async (dispatch) => {
  try {
    const data = await apiClient.get(appendPath(auth.getUserById, _id));
    return resolveAction(dispatch, types.GET_USER_INFO, data);
  } catch (error) {
    return rejectWithError(error);
  }
};

export const forgotPassword = (payload) => async (dispatch) => {
  try {
    const data = await apiClient.post(auth.resetPassword, payload);
    return resolveAction(dispatch, types.SEND_PASSWORD_RESET_REQUEST, data);
  } catch (error) {
    return rejectWithError(error);
  }
};

export const forgotEmail = (phone) => async (dispatch) => {
  try {
    const data = await apiClient.post(auth.forgotEmail, phone);
    return resolveAction(dispatch, types.SEND_PASSWORD_RESET_REQUEST, data);
  } catch (error) {
    return rejectWithError(error);
  }
};

export const getResetPasswordConfirmation = (token) => async (dispatch) => {
  try {
    const data = await apiClient.get(`${auth.resetPassword}/updatepassword`, {
      query: { token }
    });
    return resolveAction(dispatch, types.SEND_PASSWORD_RESET_REQUEST, data);
  } catch (error) {
    return rejectWithError(error);
  }
};

export const resetPassword = (user) => async (dispatch) => {
  try {
    const data = await apiClient.post(auth.resetPassword, user);
    return resolveAction(dispatch, types.RESET_PASSWORD, data);
  } catch (error) {
    return rejectWithError(error);
  }
};

export const updatePassword = (user) => async (dispatch) => {
  try {
    const data = await apiClient.post(auth.updatePassword, user, {
      query: { type: "password_update" }
    });
    return resolveAction(dispatch, types.RESET_PASSWORD, data);
  } catch (error) {
    return rejectWithError(error);
  }
};

export const setRettedAt = (token) => async (dispatch) => {
  try {
    const data = await apiClient.post(`${auth.resetPassword}/setResettedAt`, token);
    return resolveAction(dispatch, types.RESET_PASSWORD, data);
  } catch (error) {
    return rejectWithError(error);
  }
};

const confirmUserByType = (token, type) => async (dispatch) => {
  try {
    const responsePayload = await apiClient.get(auth.confirmUser, {
      query: { token, type },
      returnResponse: true
    });
    const response = createResponseLike(responsePayload);
    dispatch({ type: types.CONFIRM_USER, payload: response });
    return response;
  } catch (error) {
    return rejectWithError(error);
  }
};

export const confirmUser = (token) => confirmUserByType(token, "register");

export const resetConfirmUser = (token) => confirmUserByType(token, "password-reset");

export const confirmInstructor = (token) => async (dispatch) => {
  try {
    const data = await apiClient.get(auth.confirmInstructor, {
      query: { token }
    });
    return resolveAction(dispatch, types.CONFIRM_INSTRUCTOR, data);
  } catch (error) {
    return rejectWithError(error);
  }
};

export const editStudent = (user) => async (dispatch) => {
  try {
    const data = await apiClient.post("/api/user/update", user, { credentials: "include" });
    return resolveAction(dispatch, types.UPDATE_STUDENT, data);
  } catch (error) {
    return rejectWithError(error);
  }
};

export const updateUser = (user) => async () => {
  try {
    return await apiClient.post("/api/user/update", user, { credentials: "include" });
  } catch (error) {
    return rejectWithError(error);
  }
};

export const uploadVideo = (file) => async (dispatch) => {
  if (!hasValidToken()) {
    dispatch({ type: types.TOKEN_EXPIRED, error: "Token is expired" });
    return Promise.reject("Token is expired");
  }

  try {
    const data = await apiClient.upload(upload.uploadProfilePicture, buildVideoUploadFormData(file));
    return resolveAction(dispatch, types.VIDEO_UPLOAD_SUCCESS, data);
  } catch (error) {
    dispatch({
      type: types.VIDEO_UPLOAD_FAILURE,
      error: "Error uploading video."
    });
    return rejectWithError(error);
  }
};

export const updateStudentInfo = (user) => async (dispatch) => {
  try {
    const data = await apiClient.post(auth.updateUser, user, { credentials: "include" });
    return resolveAction(dispatch, types.UPDATE_STUDENT, data);
  } catch (error) {
    return rejectWithError(error);
  }
};

export const addCartItemToUserDocument = (email, course, schedule) => async () => {
  if (!hasValidToken()) {
    return Promise.reject("Token is expired");
  }

  try {
    const data = await apiClient.post(
      appendPath(auth.addItemToCart, email),
      buildUserCartItemPayload(email, course, schedule)
    );
    return unwrapApiResponse(data);
  } catch (error) {
    return rejectWithError(error);
  }
};

export const removeCartItemFromUserDocument = async (email, course, schedule) => {
  if (!hasValidToken()) {
    return Promise.reject("Token is expired");
  }

  try {
    const data = await apiClient.post(
      appendPath(auth.removeItemFromCart, email),
      buildUserCartItemPayload(email, course, schedule)
    );
    return unwrapApiResponse(data);
  } catch (error) {
    return rejectWithError(error);
  }
};

export const removeAllCartItemsFromUser = (email) => async () => {
  if (!hasValidToken()) {
    return Promise.reject("Token is expired");
  }

  try {
    const data = await apiClient.post(appendPath(auth.removeAllItemsFromCart, email));
    return unwrapApiResponse(data);
  } catch (error) {
    return rejectWithError(error);
  }
};

export const fetchAccountDetails = async (id) => {
  try {
    const data = await apiClient.get(appendPath(auth.getAccount, id), {
      credentials: "include"
    });
    if (data?.code === 200 && data?.status === "Success") {
      return data.data || {};
    }
    return data?.data || data;
  } catch (error) {
    return rejectWithError(error);
  }
};

export const addShoppingCartItems = async (lineItemOrCartId, product) => {
  try {
    return await apiClient.post(
      auth.addShoppingCartItem,
      buildShoppingCartLineItemPayload(lineItemOrCartId, product),
      { credentials: "include" }
    );
  } catch (error) {
    return rejectWithError(error);
  }
};

export const setReturnURL = async (protocol, host, countryCode) => {
  try {
    return await executeApiRequest(
      keys.PAYMENT_URL,
      appendPath(payment.setReturnURL, protocol, host, countryCode),
      {
        method: "POST",
        credentials: "same-origin",
        skipAuth: true
      }
    );
  } catch (error) {
    console.error("Payment return URL setup failed:", error);
    throw toUserFacingPaymentError(error);
  }
};

export const initiatePayment = async (paymentData) => {
  const request = () => executeApiRequest(keys.PAYMENT_URL, payment.pay, {
    method: "POST",
    payload: paymentData,
    credentials: "same-origin",
    skipAuth: true
  });

  try {
    return await request();
  } catch (firstError) {
    if (!isTransientPaymentError(firstError)) {
      throw toUserFacingPaymentError(firstError);
    }

    try {
      return await request();
    } catch (secondError) {
      console.error("Payment initiation failed after retry:", {
        firstError: firstError?.message,
        secondError: secondError?.message,
        status: secondError?.status
      });
      throw toUserFacingPaymentError(secondError);
    }
  }
};

export const retrieveStudentAttendance = async (studentId, scheduledProductId, companyId) => {
  try {
    const data = await apiClient.post(
      attendance.retrieveAttendance,
      buildStudentAttendanceCriteria(studentId, scheduledProductId, companyId),
      { credentials: "include" }
    );
    return unwrapApiResponse(data);
  } catch (error) {
    return rejectWithError(error);
  }
};

export const getUserByCompanyCode = (companyId) => async (dispatch) => {
  try {
    const data = await apiClient.post(
      users.getUsers,
      buildCompanyUserCriteria(companyId),
      { credentials: "include" }
    );
    return resolveAction(dispatch, types.GET_USER_BY_COMPANY_CODE_SUCCESS, data);
  } catch (error) {
    return rejectAction(dispatch, types.GET_USER_BY_COMPANY_CODE_FAIL, error);
  }
};

export const uploadContent = async (file, companyFolderId, contentType) => {
  try {
    return await apiClient.upload(
      upload.uploadImage,
      buildUploadContentFormData(file, companyFolderId, contentType),
      { credentials: "include" }
    );
  } catch (error) {
    return rejectWithError(error);
  }
};

export const createUserCurriculum = (userCurriculumData) => async (dispatch) => {
  try {
    const data = await apiClient.post(
      curriculum.createUserCurriculum,
      userCurriculumData,
      { credentials: "include" }
    );
    return resolveAction(dispatch, types.CREATE_CURRICULUM_SUCCESS, data);
  } catch (error) {
    return rejectWithError(error);
  }
};
